import type { BaseColor, Size } from '@/types/piclick';
import { formatBRL } from '@/lib/price';

type Props = {
  sizes: Size[];
  baseColors: BaseColor[];
  selectedSizeId: string | null;
  selectedBaseColorId: string | null;
  onSelectSize: (id: string) => void;
  onSelectBaseColor: (id: string) => void;
};

export function BaseSection({
  sizes,
  baseColors,
  selectedSizeId,
  selectedBaseColorId,
  onSelectSize,
  onSelectBaseColor,
}: Props) {
  return (
    <div className="space-y-8">
        <div>
          <h4 className="mb-4 font-action text-xs font-bold uppercase tracking-wide text-plum/70">
            Tamanho
          </h4>
          <ul className="grid grid-cols-3 gap-2 sm:gap-3">
            {sizes.map((size) => {
              const isSelected = selectedSizeId === size.id;
              return (
                <li key={size.id}>
                  <button
                    type="button"
                    onClick={() => onSelectSize(size.id)}
                    aria-pressed={isSelected}
                    className={`flex w-full flex-col items-center gap-2 rounded-[1.25rem] border-2 p-3 transition-colors sm:p-4 ${
                      isSelected
                        ? 'border-plum bg-butter'
                        : 'border-plum/20 bg-cream/40 hover:border-plum/50 active:bg-cream'
                    }`}
                  >
                    <span className="font-action text-base font-bold text-plum sm:text-lg">
                      {size.label}
                    </span>
                    <span
                      className="flex items-center gap-1"
                      aria-hidden="true"
                    >
                      {Array.from({ length: size.keys }).map((_, i) => (
                        <span
                          key={i}
                          className={`block h-2.5 w-2.5 rounded-full border-2 ${
                            isSelected
                              ? 'border-plum bg-white'
                              : 'border-plum/40'
                          }`}
                        />
                      ))}
                    </span>
                    <span className="font-action text-xs font-bold tabular-nums text-plum/80">
                      {formatBRL(size.price)}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>

        <div>
          <h4 className="mb-4 font-action text-xs font-bold uppercase tracking-wide text-plum/70">
            Cor da Base
          </h4>
          <ul className="grid grid-cols-5 gap-3 sm:grid-cols-6 sm:gap-4">
            {baseColors.map((color) => {
              const isSelected = selectedBaseColorId === color.id;
              return (
                <li key={color.id} className="flex justify-center">
                  <button
                    type="button"
                    onClick={() => onSelectBaseColor(color.id)}
                    aria-pressed={isSelected}
                    aria-label={color.label}
                    title={color.label}
                    className="flex flex-col items-center gap-1.5"
                  >
                    <span
                      className={`block h-11 w-11 rounded-full border-[3px] transition-all sm:h-12 sm:w-12 ${
                        isSelected
                          ? 'border-plum ring-2 ring-plum ring-offset-2'
                          : 'border-plum/30'
                      }`}
                      style={{ backgroundColor: color.hex }}
                    />
                    <span className="text-center text-[10px] leading-tight text-plum/70 sm:text-xs">
                      {color.label}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
    </div>
  );
}
